import { MessageOutput } from '@quadminds/client-apis/agents';
import { MindyViewContext } from '@quadminds/shared/mindy-context';
import { chatModeType } from '../../types/Chat/Chat.types';
import { UploadDestination } from '../../types/File/File.types';

/**
 * Maps input parameters to a `MessageOutput` object.
 *
 * This function is used to create a `MessageOutput` object based on the provided input object containing
 * the message, mode, user ID, conversation ID, and user alias. It ensures that the required fields (`query` and `agent_id`)
 * are always included, while optionally adding `user_id`, `conversation_id`, `has_files`, and `user_alias` if they are provided.
 *
 * @param input - An object containing the following properties:
 *   - message: The query or message string to be sent.
 *   - mode: The chat mode (e.g., 'knowledge' or 'logistics') that determines the agent handling the query.
 *   - userId: (Optional) The ID of the user sending the message.
 *   - conversationId: (Optional) The ID of the conversation to associate the message with.
 *   - hasFile: (Optional) Whether the message includes file attachments.
 *   - agentId: (Optional) The ID of the agent to handle the message.
 *   - userAlias: (Optional) The user's preferred nickname/alias for personalized responses.
 * @returns A `MessageOutput` object containing the mapped data.
 */
export const fromMessageModeToMessageOutput = ({
    message,
    mode: _mode,
    impersonated_user_email,
    original_user_id,
    conversationId,
    hasFile,
    agentId,
    userAlias,
    is_impersonated,
    mindy_version,
    source,
    query_context,
    knowledgeDestination,
}: {
    message: string;
    mode: chatModeType;
    impersonated_user_email?: string;
    original_user_id?: string;
    mindy_version: string;
    userId?: string;
    conversationId?: string;
    hasFile?: boolean;
    agentId?: string;
    userAlias?: string;
    is_impersonated: boolean;
    source?: string;
    /** Contexto de pantalla del SaaS (QS-19310). Sólo se manda con source === 'embedded'. */
    query_context?: MindyViewContext;
    knowledgeDestination?: UploadDestination;
}): MessageOutput => {
    const response: MessageOutput = {
        query: message,
        agent_id: agentId,
        is_impersonated: is_impersonated,
        impersonated_user_email: impersonated_user_email,
        original_user_id: original_user_id,
        mindy_version,
        source,
        query_context,
    };

    if (conversationId) {
        response.conversation_id = conversationId;
    }
    if (hasFile) {
        response.has_files = hasFile;
    }
    if (userAlias) {
        response.user_alias = userAlias;
    }
    if (knowledgeDestination) {
        response.knowledge_destination = knowledgeDestination;
    }
    return response;
};
